/** @jsx jsx */
import { Fragment, useContext } from 'react';

import { css, jsx } from '@emotion/react';

import { Box, Stack } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

import {
  ActiveTheme,
  TokenColumnWidth,
  TransformedTokenGrouped,
  TransformedTokenMerged,
} from '../types';

import TokenButtonValue from './token-button-value';
import TokenDescription from './token-description';
import TokenLifecycle from './token-lifecycle';
import TokenName from './token-name';
import {
  TableColumnsContext,
  tableTokenColumnWidthStyles,
} from './token-table';

interface TokenDefinitionProps {
  token?: TransformedTokenGrouped;
  shouldHideDescription?: boolean;
  isLoading?: boolean;
  testId?: string;
}

export type TokenValue = {
  name: string;
  value: TransformedTokenMerged['value'];
  attributes: TransformedTokenMerged['attributes'];
  original: TransformedTokenMerged['original'];
  testId: string;
  forceTheme?: ActiveTheme;
  width: TokenColumnWidth;
};

const tokenCellStyles = css({
  width: '80%',
  [media.below.sm]: {
    width: '100%',
  },
});

const resetTableDefaultStyles = css({
  padding: 0,
});

/**
 * Represents a single token consisting of token name, description, and its value(s).
 *
 * Values can be a single value for spacing, or two values (light and dark) for color.
 *
 * For tokens with extensions, the description for the token grouping is rendered by `TokenRow` for the whole grouping (rather than for each definition).
 */
const TokenDefinition = ({
  token,
  shouldHideDescription,
  isLoading,
  testId,
}: TokenDefinitionProps) => {
  const columns = useContext(TableColumnsContext);

  if (!token) {
    return null;
  }

  const group = token.attributes.group;
  const isNumericToken = group === 'spacing' || group === 'shape';

  const pixelValue =
    token.attributes.state === 'active' && isNumericToken && token.value;

  const tokenValues = columns
    .slice(1)
    .map(({ label, forceTheme, width = 'medium' }) => {
      const t =
        !isNumericToken && forceTheme?.colorMode === 'dark' && !!token.darkToken
          ? token.darkToken
          : token;
      const value = isNumericToken ? pixelValue : t.value;
      const testValue =
        group === 'typography'
          ? t.cleanName
          : isNumericToken
          ? pixelValue
          : t.original.value;
      return {
        name: label,
        value,
        attributes: t.attributes,
        original: t.original,
        testId: `${testId}-token-item-value-${
          isLoading || pixelValue === undefined ? 'loading' : testValue
        }`,
        forceTheme,
        width,
      } as TokenValue;
    });

  return (
    <Fragment>
      <td css={[resetTableDefaultStyles, tokenCellStyles]}>
        <Box>
          <Stack space="space.150">
            <Stack space="space.100">
              <TokenName name={token.name} attributes={token.attributes} />
              {!shouldHideDescription && (
                <TokenDescription transformedToken={token} />
              )}
            </Stack>
            {!shouldHideDescription && (
              <TokenLifecycle transformedToken={token} />
            )}
          </Stack>
        </Box>
      </td>
      {tokenValues.map(tokenValue => (
        <td
          key={tokenValue.name}
          css={[
            resetTableDefaultStyles,
            tableTokenColumnWidthStyles[tokenValue.width],
          ]}
        >
          <TokenButtonValue tokenValue={tokenValue} tokenOriginal={token} />
        </td>
      ))}
    </Fragment>
  );
};

export default TokenDefinition;
